import { useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  NavigationPageItemDto,
  VegaChartDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import { FullscreenWidgetModal } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  activeCustomFilterColumnIds,
  fetchFilterCustomization,
  filterCustomizationQueryKey,
} from '../../api/portalGateway/tableFilterCustomization';
import { useFullscreenInstanceIds } from '../../hooks/useFullscreenInstanceIds';
import { useStore } from '../../store';
import { useDraftCustomFilterColumnIds } from '../../store/states/dashboardEditMode';
import { getTableFilterInstanceId } from '../../utils/filterInstanceId';
import type { DashboardFilterSelections } from '../customizableDashboard/dashboardTileFilters';
import { InfoIconModal } from '../infoicon/InfoIconModal';
import { TableCustomFilter } from '../tablecustomization/TableCustomFilter';

import {
  getInitialControlState,
  vegaControlReducer,
} from './inputBinding/vegaControlState';
import { useVegaSchemeColors } from './chartColors';
import { useWithPagination } from './paginationHooks';
import { prepareSpecs } from './prepareSpecs';
import { VegaChartContent } from './VegaChartContent';

export type VegaChartProps = Pick<
  WidgetDto,
  'allowOpenInFullscreen' | 'id' | 'title' | 'collapsible' | 'infoIcon'
> &
  Pick<NavigationPageItemDto, 'pageId'> & {
    entity: VegaChartDto;
    instanceId: string;
    dashboardId?: string | null;
    dashboardContentId?: string;
    contentSourcePageId?: string;
    savedPageFilterSelections?: DashboardFilterSelections;
    savedCustomTableFilterSelections?: DashboardFilterSelections;
  };

export function VegaChart({
  allowOpenInFullscreen,
  collapsible = false,
  entity,
  id: widgetId,
  infoIcon,
  title,
  instanceId,
  pageId,
  dashboardId = null,
  dashboardContentId,
  contentSourcePageId,
  savedPageFilterSelections,
  savedCustomTableFilterSelections,
}: VegaChartProps) {
  // Per-tile isolation on dashboards; falls back to content id on normal pages.
  const localWidgetId = dashboardContentId ?? widgetId;

  const { textColor, primaryColor, secondaryColor, masterColors } =
    useVegaSchemeColors();

  const { nonFullscreenSpec, fullscreenSpec, bindings } = useMemo(() => {
    return prepareSpecs(JSON.parse(entity.config.vegaSpecification), {
      masterColors,
      textColor,
      primaryColor,
      secondaryColor,
    });
  }, [
    entity.config.vegaSpecification,
    masterColors,
    textColor,
    primaryColor,
    secondaryColor,
  ]);

  const [controlState, controlDispatch] = useReducer(
    vegaControlReducer,
    bindings,
    getInitialControlState
  );

  const [fullscreenControlState, fullscreenControlDispatch] = useReducer(
    vegaControlReducer,
    bindings,
    getInitialControlState
  );

  useEffect(() => {
    controlDispatch({
      type: 'reset',
      payload: getInitialControlState(bindings),
    });
    // this should not be called on theme change
    // we should consider splitting the prepareSpecs function to remove theme dependency from bindings reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.config.vegaSpecification]);

  const labelData = useMemo(() => {
    const labelData = Object.entries(entity.config.labels ?? {}).map(
      ([key, label]) => ({
        key,
        label,
      })
    );

    // spec label data is the same between fullscreen and non fullscreen specs, so we can use either here to extract it
    const specData = nonFullscreenSpec.data ?? [];
    const specLabelData = specData.find((d) => d.name === 'labelData');

    if (
      specLabelData != null &&
      'values' in specLabelData &&
      Array.isArray(specLabelData.values)
    ) {
      for (const labelValue of specLabelData.values) {
        const exists = labelData.some(({ key }) => key === labelValue.key);

        if (!exists) {
          labelData.push(labelValue);
        }
      }
    }

    return labelData;
  }, [entity.config.labels, nonFullscreenSpec.data]);

  const customFilterColumnIds = useMemo(
    () =>
      entity.config.customFilter.map(
        (customFilterConfig) => customFilterConfig.id
      ),
    [entity.config.customFilter]
  );

  const draftCustomFilterColumnIds = useDraftCustomFilterColumnIds(
    dashboardId,
    dashboardContentId
  );
  const {
    data: storedCustomFilterIds,
    isLoading: isFilterCustomizationLoading,
  } = useQuery({
    queryKey: filterCustomizationQueryKey('content', localWidgetId),
    queryFn: fetchFilterCustomization,
    enabled:
      entity.config.customFilter.length > 0 &&
      draftCustomFilterColumnIds == null,
  });

  const activeCustomFilterIds = useMemo(
    () =>
      activeCustomFilterColumnIds(
        draftCustomFilterColumnIds,
        storedCustomFilterIds?.data?.columns.map((column) => column.columnId),
        customFilterColumnIds
      ),
    [
      customFilterColumnIds,
      draftCustomFilterColumnIds,
      storedCustomFilterIds?.data?.columns,
    ]
  );

  const [
    customFilterOpened,
    { close: closeCustomFilter, open: openCustomFilter },
  ] = useDisclosure();

  const {
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    customPageFilterStoreId,
    cloneFullscreenInstanceState,
  } = useFullscreenInstanceIds({
    pageFilterInstanceId: instanceId,
    widgetFilterInstanceId: localWidgetId,
  });

  const [fullscreenOpened, { close: closeFullscreen, open: openFullscreen }] =
    useDisclosure(false, {
      onOpen: () => {
        cloneFullscreenInstanceState();
        fullscreenControlDispatch({ type: 'reset', payload: controlState });
      },
    });

  const [infoModalOpened, { close: closeInfoModal, open: openInfoModal }] =
    useDisclosure();

  const withPagination = useWithPagination(nonFullscreenSpec);
  const [resetPagination, setPerPage] = useStore(
    useShallow((state) => [state.resetPagination, state.setPerPage])
  );
  const resetPaginationAndOpenFullscreen = useCallback(() => {
    resetPagination(widgetFullscreenInstanceStateId);
    setPerPage(fullscreenInstanceStateId, widgetFullscreenInstanceStateId, 50);
    openFullscreen();
  }, [
    widgetFullscreenInstanceStateId,
    openFullscreen,
    resetPagination,
    setPerPage,
    fullscreenInstanceStateId,
  ]);

  return (
    <>
      <VegaChartContent
        activeCustomFilterIds={activeCustomFilterIds}
        bindings={bindings}
        collapsible={collapsible}
        controlDispatch={controlDispatch}
        controlState={controlState}
        customFilterConfigs={entity.config.customFilter}
        enableScrollElementToTop
        entity={entity}
        infoIcon={infoIcon}
        isFullscreen={false}
        labelData={labelData}
        openCustomFilter={openCustomFilter}
        openFullscreen={
          allowOpenInFullscreen ? resetPaginationAndOpenFullscreen : undefined
        }
        openInfoModal={openInfoModal}
        pageFilterInstanceId={instanceId}
        pageId={pageId}
        dashboardId={dashboardId}
        dashboardContentId={dashboardContentId}
        contentSourcePageId={contentSourcePageId}
        isFilterCustomizationLoading={isFilterCustomizationLoading}
        savedPageFilterSelections={savedPageFilterSelections}
        savedCustomTableFilterSelections={savedCustomTableFilterSelections}
        spec={nonFullscreenSpec}
        title={title}
        widgetId={widgetId}
        withPagination={withPagination}
        widgetInstanceId={localWidgetId}
      />
      {allowOpenInFullscreen && (
        <FullscreenWidgetModal
          onClose={closeFullscreen}
          opened={fullscreenOpened}
        >
          <VegaChartContent
            activeCustomFilterIds={activeCustomFilterIds}
            bindings={bindings}
            closeFullscreen={closeFullscreen}
            collapsible={collapsible}
            controlDispatch={fullscreenControlDispatch}
            controlState={fullscreenControlState}
            customFilterConfigs={entity.config.customFilter}
            dataTestId="vega-chart_widget_fullscreen"
            infoIcon={infoIcon}
            isFullscreen={true}
            customPageFilterStoreId={customPageFilterStoreId}
            entity={entity}
            labelData={labelData}
            openCustomFilter={openCustomFilter}
            openInfoModal={openInfoModal}
            pageFilterInstanceId={fullscreenInstanceStateId}
            pageId={pageId}
            dashboardId={dashboardId}
            dashboardContentId={dashboardContentId}
            contentSourcePageId={contentSourcePageId}
            isFilterCustomizationLoading={isFilterCustomizationLoading}
            savedPageFilterSelections={savedPageFilterSelections}
            savedCustomTableFilterSelections={savedCustomTableFilterSelections}
            showPageFilter={true}
            spec={fullscreenSpec}
            title={title}
            widgetId={widgetId}
            withPagination={false}
            widgetInstanceId={widgetFullscreenInstanceStateId}
          />
        </FullscreenWidgetModal>
      )}
      <InfoIconModal
        onClose={closeInfoModal}
        opened={infoModalOpened}
        headline={infoIcon?.headline}
        text={infoIcon?.text}
      />
      {entity.config.customFilter.length > 0 && (
        <TableCustomFilter
          activeCustomFilterIds={activeCustomFilterIds}
          customFilterColumns={entity.config.customFilter.map((filter) => ({
            id: filter.id,
            label: filter.filterConfig.label || filter.filterConfig.key,
            ...filter.filterConfig,
          }))}
          modalOpened={customFilterOpened}
          onClose={closeCustomFilter}
          reference={localWidgetId}
          referenceType="content"
          filterInstanceIds={[
            getTableFilterInstanceId(localWidgetId, instanceId),
            getTableFilterInstanceId(
              widgetFullscreenInstanceStateId,
              fullscreenInstanceStateId
            ),
          ]}
        />
      )}
    </>
  );
}
